import type { ErpConfig } from "@/pages/ErpResourcePage";

export const ERP_CONFIGS: Record<string, ErpConfig> = {
  boms: {
    resource: "boms", titleKey: "nav.boms", subtitleKey: "erpResource.subtitles.boms", soft: true,
    columns: [
      { key: "productCode", labelKey: "erpResource.fields.product", kind: "mono" },
      { key: "version", labelKey: "erpResource.fields.version" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "badge", tones: { Active: "ok", Draft: "neutral", Obsolete: "danger" } },
    ],
    fields: [
      { key: "productCode", labelKey: "erpResource.fields.productCode" },
      { key: "version", labelKey: "erpResource.fields.version" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "select", options: ["Draft", "Active", "Obsolete"] },
    ],
    statusField: "status", statusOptions: ["Draft", "Active", "Obsolete"],
  },
  "sales-orders": {
    resource: "sales-orders", titleKey: "nav.salesOrders", subtitleKey: "erpResource.subtitles.salesOrders", soft: true,
    columns: [
      { key: "soNo", labelKey: "erpResource.fields.soNo", kind: "mono" },
      { key: "orderDate", labelKey: "erpResource.fields.orderDate", kind: "date" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "badge", tones: { Draft: "neutral", Confirmed: "info", "In Production": "warn", Shipped: "info", Closed: "ok", Cancelled: "danger" } },
      { key: "total", labelKey: "erpResource.fields.total", kind: "money", align: "right" },
    ],
    fields: [
      { key: "customerId", labelKey: "erpResource.fields.customer", kind: "customer" },
      { key: "orderDate", labelKey: "erpResource.fields.orderDate", kind: "date" },
      { key: "total", labelKey: "erpResource.fields.total", kind: "money" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "select", options: ["Draft", "Confirmed", "In Production", "Shipped", "Closed", "Cancelled"] },
      { key: "remarks", labelKey: "erpResource.fields.remarks" },
    ],
    statusField: "status", statusOptions: ["Draft", "Confirmed", "In Production", "Shipped", "Closed", "Cancelled"],
  },
  "purchase-requisitions": {
    resource: "purchase-requisitions", titleKey: "nav.requisitions", subtitleKey: "erpResource.subtitles.requisitions",
    columns: [
      { key: "prNo", labelKey: "erpResource.fields.prNo", kind: "mono" },
      { key: "neededBy", labelKey: "erpResource.fields.neededBy", kind: "date" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "badge", tones: { Draft: "neutral", Approved: "info", Ordered: "warn", Closed: "ok" } },
    ],
    fields: [
      { key: "neededBy", labelKey: "erpResource.fields.neededBy", kind: "date" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "select", options: ["Draft", "Approved", "Ordered", "Closed"] },
      { key: "remarks", labelKey: "erpResource.fields.remarks" },
    ],
    soft: true, statusField: "status", statusOptions: ["Draft", "Approved", "Ordered", "Closed"],
  },
  warehouses: {
    resource: "warehouses", titleKey: "nav.warehouse", subtitleKey: "erpResource.subtitles.warehouses",
    columns: [
      { key: "code", labelKey: "erpResource.fields.code", kind: "mono" },
      { key: "name", labelKey: "erpResource.fields.name" },
      { key: "location", labelKey: "erpResource.fields.location" },
    ],
    fields: [
      { key: "code", labelKey: "erpResource.fields.code" },
      { key: "name", labelKey: "erpResource.fields.name" },
      { key: "location", labelKey: "erpResource.fields.location" },
    ],
  },
  "stock-moves": {
    resource: "stock-moves", titleKey: "nav.stockMoves", subtitleKey: "erpResource.subtitles.stockMoves",
    columns: [
      { key: "moveNo", labelKey: "erpResource.fields.moveNo", kind: "mono" },
      { key: "productCode", labelKey: "erpResource.fields.product", kind: "mono" },
      { key: "moveType", labelKey: "erpResource.fields.type", kind: "badge", tones: { in: "ok", out: "danger", transfer: "info", adjust: "warn" } },
      { key: "quantity", labelKey: "erpResource.fields.qty", align: "right" },
      { key: "movedAt", labelKey: "erpResource.fields.when", kind: "date" },
    ],
    fields: [
      { key: "productCode", labelKey: "erpResource.fields.productCode" },
      { key: "moveType", labelKey: "erpResource.fields.type", kind: "select", options: ["in", "out", "transfer", "adjust"] },
      { key: "quantity", labelKey: "erpResource.fields.quantity", kind: "number" },
      { key: "reference", labelKey: "erpResource.fields.reference" },
    ],
  },
  "work-orders": {
    resource: "work-orders", titleKey: "nav.production", subtitleKey: "erpResource.subtitles.workOrders", soft: true,
    columns: [
      { key: "woNo", labelKey: "erpResource.fields.woNo", kind: "mono" },
      { key: "productCode", labelKey: "erpResource.fields.product", kind: "mono" },
      { key: "plannedQty", labelKey: "erpResource.fields.planned", align: "right" },
      { key: "producedQty", labelKey: "erpResource.fields.produced", align: "right" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "badge", tones: { Planned: "neutral", Released: "info", "In Progress": "warn", Done: "ok", Cancelled: "danger" } },
    ],
    fields: [
      { key: "productCode", labelKey: "erpResource.fields.productCode" },
      { key: "description", labelKey: "erpResource.fields.description" },
      { key: "plannedQty", labelKey: "erpResource.fields.plannedQty", kind: "number" },
      { key: "dueDate", labelKey: "erpResource.fields.dueDate", kind: "date" },
      { key: "status", labelKey: "erpResource.fields.status", kind: "select", options: ["Planned", "Released", "In Progress", "Done", "Cancelled"] },
    ],
    statusField: "status", statusOptions: ["Planned", "Released", "In Progress", "Done", "Cancelled"],
  },
  inspections: {
    resource: "inspections", titleKey: "nav.quality", subtitleKey: "erpResource.subtitles.inspections",
    columns: [
      { key: "inspNo", labelKey: "erpResource.fields.qcNo", kind: "mono" },
      { key: "inspType", labelKey: "erpResource.fields.type", kind: "badge" },
      { key: "productCode", labelKey: "erpResource.fields.product", kind: "mono" },
      { key: "result", labelKey: "erpResource.fields.result", kind: "badge", tones: { Pending: "neutral", Pass: "ok", Fail: "danger" } },
      { key: "qtyRejected", labelKey: "erpResource.fields.rejected", align: "right" },
    ],
    fields: [
      { key: "inspType", labelKey: "erpResource.fields.type", kind: "select", options: ["Incoming", "In-Process", "Final"] },
      { key: "productCode", labelKey: "erpResource.fields.productCode" },
      { key: "reference", labelKey: "erpResource.fields.referencePoWo" },
      { key: "qtyInspected", labelKey: "erpResource.fields.qtyInspected", kind: "number" },
      { key: "qtyRejected", labelKey: "erpResource.fields.qtyRejected", kind: "number" },
      { key: "result", labelKey: "erpResource.fields.result", kind: "select", options: ["Pending", "Pass", "Fail"] },
      { key: "notes", labelKey: "erpResource.fields.notes" },
    ],
    statusField: "result", statusOptions: ["Pending", "Pass", "Fail"],
  },
  "gl-accounts": {
    resource: "gl-accounts", titleKey: "nav.glAccounts", subtitleKey: "erpResource.subtitles.glAccounts",
    columns: [
      { key: "code", labelKey: "erpResource.fields.code", kind: "mono" },
      { key: "name", labelKey: "erpResource.fields.name" },
      { key: "type", labelKey: "erpResource.fields.accountType", kind: "badge", tones: { Asset: "info", Liability: "warn", Equity: "brand", Revenue: "ok", Expense: "danger" } },
    ],
    fields: [
      { key: "code", labelKey: "erpResource.fields.code" },
      { key: "name", labelKey: "erpResource.fields.name" },
      { key: "type", labelKey: "erpResource.fields.accountType", kind: "select", options: ["Asset", "Liability", "Equity", "Revenue", "Expense"] },
    ],
  },
  "journal-entries": {
    resource: "journal-entries", titleKey: "nav.finance", subtitleKey: "erpResource.subtitles.journalEntries",
    columns: [
      { key: "jeNo", labelKey: "erpResource.fields.jeNo", kind: "mono" },
      { key: "date", labelKey: "erpResource.fields.date", kind: "date" },
      { key: "memo", labelKey: "erpResource.fields.memo" },
      { key: "debitCode", labelKey: "erpResource.fields.debit", kind: "mono" },
      { key: "creditCode", labelKey: "erpResource.fields.credit", kind: "mono" },
      { key: "amount", labelKey: "erpResource.fields.amount", kind: "money", align: "right" },
    ],
    fields: [
      { key: "date", labelKey: "erpResource.fields.date", kind: "date" },
      { key: "memo", labelKey: "erpResource.fields.memo" },
      { key: "debitCode", labelKey: "erpResource.fields.debitAccount" },
      { key: "creditCode", labelKey: "erpResource.fields.creditAccount" },
      { key: "amount", labelKey: "erpResource.fields.amount", kind: "money" },
      { key: "sourceRef", labelKey: "erpResource.fields.sourceRef" },
    ],
  },
  "service-tickets": {
    resource: "service-tickets", titleKey: "nav.serviceTickets", subtitleKey: "erpResource.subtitles.serviceTickets", soft: true,
    columns: [
      { key: "ticketNo", labelKey: "erpResource.fields.ticket", kind: "mono" },
      { key: "subject", labelKey: "erpResource.fields.subject" },
      { key: "priority", labelKey: "erpResource.fields.priority", kind: "badge", tones: { Low: "neutral", Medium: "info", High: "warn", Urgent: "danger" } },
      { key: "status", labelKey: "erpResource.fields.status", kind: "badge", tones: { Open: "info", "In Progress": "warn", Resolved: "ok", Closed: "neutral" } },
    ],
    fields: [
      { key: "customerId", labelKey: "erpResource.fields.customer", kind: "customer" },
      { key: "subject", labelKey: "erpResource.fields.subject" },
      { key: "description", labelKey: "erpResource.fields.description" },
      { key: "priority", labelKey: "erpResource.fields.priority", kind: "select", options: ["Low", "Medium", "High", "Urgent"] },
      { key: "status", labelKey: "erpResource.fields.status", kind: "select", options: ["Open", "In Progress", "Resolved", "Closed"] },
    ],
    statusField: "status", statusOptions: ["Open", "In Progress", "Resolved", "Closed"],
  },
};
